<script setup lang="ts" generic="T">
  import { computed } from 'vue';
  import { IconImage } from '@/shared/ui';
  import AddButton from '@/shared/assets/icons/AddButton.svg';
  import DeleteButton from '@/shared/assets/icons/DeleteButton.svg';

  const props = withDefaults(
    defineProps<{
      modelValue: T[];
      emptyValue: T;
      max?: number;
      addLabel?: string;
      removeLabel?: string;
      disabled?: boolean;
    }>(),
    { max: undefined, addLabel: 'Adicionar', removeLabel: 'Remover', disabled: false }
  );

  const emit = defineEmits<{
    'update:modelValue': [value: T[]];
  }>();

  defineSlots<{
    default(props: { value: T; index: number; update: (value: T) => void }): unknown;
  }>();

  const rows = computed<T[]>(() =>
    props.modelValue.length > 0 ? props.modelValue : [props.emptyValue]
  );

  const canAdd = computed(
    () => !props.disabled && (props.max === undefined || rows.value.length < props.max)
  );

  function update(index: number, value: T): void {
    const next = [...rows.value];
    next[index] = value;
    emit('update:modelValue', next);
  }

  function add(): void {
    if (!canAdd.value) return;
    emit('update:modelValue', [...rows.value, props.emptyValue]);
  }

  function remove(index: number): void {
    emit(
      'update:modelValue',
      rows.value.filter((_, rowIndex) => rowIndex !== index)
    );
  }
</script>

<template>
  <div class="flex flex-col gap-3" data-testid="record-config-repeater">
    <div
      v-for="(value, index) in rows"
      :key="index"
      class="flex items-center justify-between gap-2"
      data-testid="record-config-repeater-row"
    >
      <div class="min-w-0">
        <slot :value="value" :index="index" :update="(next: T) => update(index, next)" />
      </div>

      <div class="flex h-12 shrink-0 items-center text-[var(--theme-secondary)]">
        <button
          v-if="index === 0"
          type="button"
          :aria-label="addLabel"
          :disabled="!canAdd"
          class="flex items-center justify-center"
          :class="canAdd ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'"
          data-testid="record-config-repeater-add"
          @click="add"
        >
          <IconImage :src="AddButton" :alt="addLabel" />
        </button>
        <button
          v-else
          type="button"
          :aria-label="removeLabel"
          :disabled="disabled"
          class="flex cursor-pointer items-center justify-center disabled:cursor-not-allowed disabled:opacity-50"
          data-testid="record-config-repeater-remove"
          @click="remove(index)"
        >
          <IconImage :src="DeleteButton" :alt="removeLabel" />
        </button>
      </div>
    </div>
  </div>
</template>
